/**
 * V2 Step 3 — Services.
 * Design from v2-screens-1.jsx:
 *  - V2EmptyServices: centered italic prompt with Add button
 *  - V2ServiceRow: pencil + trash icon buttons, delete-confirm overlay
 *  - V2ServiceForm: NumberField for duration/price/buffer, textarea for description
 * At least 1 service required; all must be ≥ $5.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { V2C, fontBody, fontDisplay } from '../shared/tokens';
import { V2Btn, V2Label, V2InlineMsg, V2Input, V2SectionWarning } from '../shared/primitives';
import V2Step from '../shared/V2Step';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

type Service = BusinessSignupData['services'][number];

interface Props {
  formData: BusinessSignupData;
  update: (patch: Partial<BusinessSignupData>) => void;
  onBack?: () => void;
  onNext: () => void;
  stepIdx: number;
  totalSteps: number;
  animDir: 'forward' | 'back';
  validationError: string;
}

type DraftService = Service & { _tried?: boolean };

function newDraft(): DraftService {
  return {
    id: `svc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '', description: '', duration: 60, price: 50, bufferTime: 0, waitlistEnabled: false,
  };
}

export default function V2ServicesStep({ formData, update, onBack, onNext, stepIdx, totalSteps, animDir, validationError }: Props) {
  const services = (formData.services ?? []) as Service[];
  const [editingId, setEditingId] = useState<string | null>(null); // 'new' or service.id
  const [draft, setDraft] = useState<DraftService | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function startAdd() {
    const d = newDraft();
    setDraft(d);
    setEditingId('new');
  }

  function startEdit(svc: Service) {
    setDraft({ ...svc });
    setEditingId(svc.id);
  }

  function saveDraft() {
    if (!draft) return;
    if (!draft.name.trim()) { setDraft(d => d ? { ...d, _tried: true } : d); return; }
    if ((draft.price ?? 0) < 5) { setDraft(d => d ? { ...d, _tried: true } : d); return; }
    if (editingId === 'new') {
      update({ services: [...services, draft] });
    } else {
      update({ services: services.map(s => s.id === draft.id ? draft : s) });
    }
    setEditingId(null);
    setDraft(null);
  }

  function cancelDraft() {
    setEditingId(null);
    setDraft(null);
  }

  function removeService(id: string) {
    update({ services: services.filter(s => s.id !== id) });
  }

  const empty = services.length === 0;

  return (
    <V2Step
      stepIdx={stepIdx}
      totalSteps={totalSteps}
      title="Your services"
      subtitle="Add at least one service before launching. Edit or remove anytime."
      onBack={onBack}
      animDir={animDir}
    >
      {/* Empty state */}
      {empty && !editingId && (
        <V2EmptyServices onAdd={startAdd} />
      )}

      {/* Service list */}
      {!empty && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {services.map(svc => (
            <V2ServiceRow
              key={svc.id}
              service={svc}
              onEdit={() => startEdit(svc)}
              onDelete={() => removeService(svc.id)}
            />
          ))}
        </div>
      )}

      {/* Add another button */}
      {!editingId && !empty && (
        <button onClick={startAdd} style={{
          width: '100%', padding: '14px', borderRadius: 14,
          border: `1.5px dashed ${V2C.brand}`,
          background: 'transparent', color: V2C.brand,
          fontFamily: fontBody, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 14,
        }}>
          + Add another service
        </button>
      )}

      {/* Inline form */}
      {editingId && draft && (
        <V2ServiceForm
          draft={draft}
          setDraft={setDraft as React.Dispatch<React.SetStateAction<DraftService | null>>}
          onSave={saveDraft}
          onCancel={cancelDraft}
          isNew={editingId === 'new'}
        />
      )}

      {/* Submitted with no services */}
      {empty && submitted && (
        <V2SectionWarning title="Add a service to continue">
          You'll need at least one bookable service before you can launch your page.
        </V2SectionWarning>
      )}

      {validationError && !submitted && (
        <div style={{ color: V2C.err, fontSize: 13, fontWeight: 600, marginBottom: 8, fontFamily: fontBody }}>
          {validationError}
        </div>
      )}

      {!editingId && (
        <div style={{ position: 'sticky', bottom: 0, paddingBottom: 24, paddingTop: 8, background: 'linear-gradient(to bottom, transparent, #F3F3FF 60%)' }}>
          <V2Btn
            disabled={empty}
            onClick={() => {
              setSubmitted(true);
              if (empty) return;
              const hasUnderpriced = services.some(s => s.price < 5);
              if (hasUnderpriced) return;
              onNext();
            }}
          >
            {empty
              ? 'Add a service to continue'
              : `Continue with ${services.length} service${services.length === 1 ? '' : 's'}`}
          </V2Btn>
        </div>
      )}
    </V2Step>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function V2EmptyServices({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 18, border: `1.5px dashed ${V2C.brand}55`,
      padding: '28px 18px', textAlign: 'center', marginBottom: 14,
    }}>
      <div style={{
        fontFamily: fontDisplay, fontSize: 24, lineHeight: 1.1, letterSpacing: -0.4, marginBottom: 6,
      }}>
        Add your <span style={{ fontStyle: 'italic', color: V2C.brand }}>first service</span>
      </div>
      <div style={{ fontSize: 13, color: V2C.mute, marginBottom: 16, lineHeight: 1.5, fontFamily: fontBody }}>
        You can add more right after.
      </div>
      <V2Btn onClick={onAdd}>+ Add your first service</V2Btn>
    </div>
  );
}

// ── Service row ───────────────────────────────────────────────────────────────
function V2ServiceRow({ service, onEdit, onDelete }: {
  service: Service;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div style={{
      background: '#fff', borderRadius: 14, border: `1px solid ${V2C.line}`,
      padding: 14, position: 'relative',
      animation: 'v2fadeIn 0.32s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: V2C.ink, fontFamily: fontBody }}>
            {service.name}
          </div>
          {service.description && (
            <div style={{ fontSize: 12, color: V2C.mute, marginTop: 2, lineHeight: 1.45, fontFamily: fontBody }}>
              {service.description}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Chip>⏱ {service.duration} min</Chip>
            <Chip>${service.price}</Chip>
            {service.bufferTime > 0 && <Chip muted>+{service.bufferTime} min buffer</Chip>}
          </div>
        </div>

        {/* Icon buttons */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <IconBtn onClick={onEdit} title="Edit">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2l3 3-7.5 7.5L1 13l.5-3.5L9 2z" stroke={V2C.mute} strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
          </IconBtn>
          <IconBtn onClick={() => setConfirming(true)} title="Delete">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 4h9m-7 0V2.5h4V4m-3 3v3m2-3v3M3 4l.5 8h7L11 4" stroke={V2C.mute} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </IconBtn>
        </div>
      </div>

      {/* Delete confirmation overlay */}
      {confirming && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 14,
          background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(2px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: 14, gap: 8,
          animation: 'v2bannerIn 0.2s ease-out',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: V2C.ink, textAlign: 'center', fontFamily: fontBody }}>
            Delete "{service.name}"?
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <V2Btn secondary small onClick={() => setConfirming(false)}>Keep</V2Btn>
            <V2Btn danger small onClick={() => { onDelete(); setConfirming(false); }}>Delete</V2Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Service form ──────────────────────────────────────────────────────────────
function V2ServiceForm({ draft, setDraft, onSave, onCancel, isNew }: {
  draft: DraftService;
  setDraft: React.Dispatch<React.SetStateAction<DraftService | null>>;
  onSave: () => void;
  onCancel: () => void;
  isNew: boolean;
}) {
  function upd<K extends keyof DraftService>(k: K, v: DraftService[K]) {
    setDraft(d => d ? { ...d, [k]: v } : d);
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: `1.5px solid ${V2C.lineHard}`,
      padding: 14, marginBottom: 14, boxSizing: 'border-box',
      boxShadow: '0 12px 30px -12px rgba(89,86,246,0.25)',
      animation: 'v2fadeIn 0.34s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
        color: V2C.brand, marginBottom: 10, fontFamily: fontBody,
      }}>
        {isNew ? 'New service' : 'Edit service'}
      </div>

      <V2Input
        label="Service name"
        value={draft.name}
        onChange={v => upd('name', v)}
        placeholder="e.g. Haircut, 60-min massage"
        autoFocus
        error={draft._tried && !draft.name.trim() ? 'Required' : null}
        onboardingField="service_name"
      />

      {/* Description */}
      <V2Label optional>Description</V2Label>
      <textarea
        value={draft.description}
        onChange={e => upd('description', e.target.value)}
        placeholder="One sentence to set expectations."
        maxLength={160}
        data-onboarding-field="service_description"
        style={{
          width: '100%', minHeight: 60, resize: 'vertical', boxSizing: 'border-box',
          background: '#fff', border: `1.5px solid ${V2C.line}`, borderRadius: 12,
          padding: 12, fontFamily: fontBody, fontSize: 14, color: V2C.ink, outline: 'none',
          marginBottom: 14, display: 'block',
        }}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, minWidth: 0 }}>
        <NumberField
          label="Duration"
          suffix="min"
          value={draft.duration}
          onChange={v => upd('duration', v)}
          min={5}
          step={5}
          onboardingField="service_duration"
        />
        <NumberField
          label="Price"
          prefix="$"
          value={draft.price}
          onChange={v => upd('price', v)}
          min={5}
          error={draft._tried && (draft.price ?? 0) < 5 ? 'Minimum $5' : null}
          onboardingField="service_price"
        />
      </div>

      <NumberField
        label="Buffer after"
        suffix="min"
        value={draft.bufferTime}
        onChange={v => upd('bufferTime', v)}
        min={0}
        step={5}
        hint="Cleanup time — auto-blocks calendar."
        onboardingField="service_buffer_time"
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 14, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <V2Btn secondary onClick={onCancel}>Cancel</V2Btn>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <V2Btn onClick={() => {
            if (!draft.name.trim()) { setDraft(d => d ? { ...d, _tried: true } : d); return; }
            onSave();
          }}>
            {isNew ? 'Add service' : 'Save'}
          </V2Btn>
        </div>
      </div>
    </div>
  );
}

// ── NumberField ───────────────────────────────────────────────────────────────
function NumberField({ label, value, onChange, prefix, suffix, min = 0, step = 1, hint, error, onboardingField }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  min?: number;
  step?: number;
  hint?: string;
  error?: string | null;
  onboardingField?: string;
}) {
  const [local, setLocal] = useState(String(value ?? 0));

  useEffect(() => {
    if (Number(local) !== value) setLocal(String(value ?? 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function commit(raw: string) {
    const cleaned = (raw || '').replace(/[^\d]/g, '');
    setLocal(cleaned);
    onChange(cleaned === '' ? 0 : parseInt(cleaned, 10));
  }

  function onBlur() {
    const num = local === '' ? 0 : parseInt(local, 10);
    setLocal(String(num));
    onChange(num);
  }

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <V2Label>{label}</V2Label>
      <div style={{
        background: '#fff', borderRadius: 12,
        border: `1.5px solid ${error ? V2C.errSoft : V2C.line}`,
        padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6,
        boxSizing: 'border-box',
        boxShadow: error ? `0 0 0 3px ${V2C.errBg}` : 'none',
      }}>
        {prefix && <span style={{ fontSize: 14, color: V2C.mute, flexShrink: 0, fontFamily: fontBody }}>{prefix}</span>}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          data-onboarding-field={onboardingField}
          value={local}
          onChange={e => commit(e.target.value)}
          onFocus={e => e.target.select()}
          onBlur={onBlur}
          style={{
            flex: 1, minWidth: 0, width: '100%',
            border: 'none', outline: 'none', background: 'transparent',
            fontFamily: fontBody, fontSize: 15, fontWeight: 600, color: V2C.ink, padding: '11px 0',
          }}
        />
        {suffix && <span style={{ fontSize: 13, color: V2C.mute, flexShrink: 0, fontFamily: fontBody }}>{suffix}</span>}
      </div>
      {error && <V2InlineMsg kind="error">{error}</V2InlineMsg>}
      {!error && hint && <div style={{ fontSize: 11, color: V2C.mute, marginTop: 4, lineHeight: 1.4, fontFamily: fontBody }}>{hint}</div>}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Chip({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 12, fontWeight: 600,
      color: muted ? V2C.mute : V2C.ink,
      background: muted ? V2C.bgAlt : V2C.brandSofter,
      padding: '4px 10px', borderRadius: 99, letterSpacing: 0.1,
      fontFamily: fontBody,
    }}>
      {children}
    </span>
  );
}

function IconBtn({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button onClick={onClick} title={title} style={{
      width: 30, height: 30, borderRadius: 8, border: `1px solid ${V2C.line}`,
      background: '#fff', cursor: 'pointer', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {children}
    </button>
  );
}
